/**
 * CustomerContext — holds the list of customers and the active workspace
 * (multi-customer, Phase 3).
 *
 * The active customer id is persisted in localStorage so a refresh keeps the
 * same workspace. Default is the customer flagged is_default (Melville). The
 * special value 'all' means the consolidated "View All" view (pages omit the
 * customer_id filter).
 *
 * Admin sees the full active-customer list; a contractor only sees the
 * customers they have access to (the provider auto-picks the endpoint based
 * on the user's type).
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api } from '../api';

const CustomerContext = createContext(null);
const STORAGE_KEY = 'acts_active_customer';

export function CustomerProvider({ user, children }) {
  const [customers, setCustomers]   = useState([]);
  const [activeId, setActiveIdState] = useState(null); // number-as-string | 'all' | null
  const [loading, setLoading]       = useState(true);

  const isAdmin = user?.type === 'admin';

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    try {
      // Admin gets every active customer; contractors only their accessible
      // ones (drives whether the switcher even shows for them).
      const data = isAdmin
        ? await api.listCustomers('active')
        : await api.myAccessibleCustomers();
      const list = Array.isArray(data) ? data : [];
      setCustomers(list);

      // Resolve the active customer: stored value if still valid, else the
      // default-flagged customer, else the first in the list.
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === 'all' && isAdmin) {
        setActiveIdState('all');
      } else if (stored && list.find(c => String(c.id) === stored)) {
        setActiveIdState(stored);
      } else {
        const def = list.find(c => c.is_default) || list[0];
        setActiveIdState(def ? String(def.id) : null);
      }
    } catch (err) {
      console.error('CustomerContext fetch failed:', err);
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => { if (user) fetchCustomers(); }, [user, fetchCustomers]);

  const setActiveId = useCallback((id) => {
    setActiveIdState(id);
    if (id) localStorage.setItem(STORAGE_KEY, String(id));
  }, []);

  const isViewAll      = activeId === 'all';
  const activeCustomer = isViewAll ? null : customers.find(c => String(c.id) === String(activeId)) || null;
  // Whether the workspace switcher should render at all: admins always; a
  // contractor only when they can serve more than one customer.
  const showSwitcher   = isAdmin ? true : customers.length > 1;

  // Convenience for API calls: the customer_id value to send (or undefined to
  // omit the filter — used in View All mode).
  const customerParam  = isViewAll ? undefined : (activeId ? String(activeId) : undefined);

  return (
    <CustomerContext.Provider value={{
      customers, activeId, activeCustomer, isViewAll, showSwitcher,
      customerParam, loading, setActiveId, refetch: fetchCustomers,
    }}>
      {children}
    </CustomerContext.Provider>
  );
}

export function useCustomer() {
  const ctx = useContext(CustomerContext);
  // Tolerate being called outside the provider (e.g. a component that renders
  // for both admin and a public page) — return inert defaults instead of
  // throwing so those callers don't need a guard.
  if (!ctx) {
    return {
      customers: [], activeId: null, activeCustomer: null, isViewAll: false,
      showSwitcher: false, customerParam: undefined, loading: false,
      setActiveId: () => {}, refetch: () => {},
    };
  }
  return ctx;
}
